import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOW } from 'styles/tokens';

interface FormSectionProps {
  /** Small uppercase kicker rendered above the title (e.g. "STEP 02"). */
  eyebrow?: string;
  title?: string;
  description?: string;
  children: ReactNode;
  /** Compact spacing — for nested sections or dense forms. */
  dense?: boolean;
  /** Render without the tonal surface (just spacing) — for full-page forms. */
  ghost?: boolean;
}

const FormSection: FC<FormSectionProps> = ({ eyebrow, title, description, dense, ghost, children }) => (
  <Box
    sx={{
      bgcolor: ghost ? 'transparent' : SURFACE_CONTAINER_LOW,
      borderRadius: ghost ? 0 : RADIUS_CARD,
      p: ghost ? 0 : dense ? { xs: 2.5, md: 3 } : { xs: 3, md: 4 },
      mb: ghost ? 5 : 3,
      fontFamily: 'Jost, sans-serif',
    }}
  >
    {(eyebrow || title || description) && (
      <Box sx={{ mb: dense ? 2.5 : 3 }}>
        {eyebrow && (
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: alpha(NAVY, 0.5),
              mb: 0.75,
            }}
          >
            {eyebrow}
          </Typography>
        )}
        {title && (
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: dense ? '1.0625rem' : '1.25rem',
              fontWeight: 700,
              color: NAVY,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              mb: description ? 0.75 : 0,
            }}
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.875rem',
              fontWeight: 400,
              color: alpha(NAVY, 0.6),
              lineHeight: 1.5,
              maxWidth: 520,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    )}
    {children}
  </Box>
);

export default FormSection;
